import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const Lightbox = ({ images, open, startIndex = 0, auto = false, title, onClose }) => {
  const [i, setI] = useState(startIndex);

  useEffect(() => { if (open) setI(startIndex); }, [open, startIndex]);

  const next = useCallback(() => setI((v) => (v + 1) % images.length), [images.length]);
  const prev = useCallback(() => setI((v) => (v - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") prev(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev, onClose]);

  useEffect(() => {
    if (!open || !auto) return;
    const id = setInterval(next, 2600);
    return () => clearInterval(id);
  }, [open, auto, next]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-md" data-testid="lightbox" onClick={onClose}>
          <button onClick={onClose} data-testid="lightbox-close" className="absolute right-6 top-6 z-10 text-paper/70 hover:text-paper"><X className="h-7 w-7" /></button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" className="absolute left-4 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-edge bg-surface/70 text-cream hover:border-gold hover:text-gold md:left-10"><ChevronLeft className="h-5 w-5" /></button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" className="absolute right-4 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-edge bg-surface/70 text-cream hover:border-gold hover:text-gold md:right-10"><ChevronRight className="h-5 w-5" /></button>

          <motion.div key={i} initial={{ opacity: 0, scale: auto ? 1.04 : 0.98 }} animate={{ opacity: 1, scale: auto ? 1.09 : 1 }}
            transition={{ duration: auto ? 2.8 : 0.4, ease: "easeOut" }} onClick={(e) => e.stopPropagation()}
            className="relative max-h-[85vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-edge">
            <img src={images[i]} alt="" className="max-h-[85vh] w-full object-cover" />
            {auto && title && (
              <div className="absolute inset-0 flex items-end bg-gradient-to-t from-ink/80 to-transparent p-8">
                <div>
                  <div className="font-body text-xs uppercase tracking-[0.3em] text-gold">Official Teaser</div>
                  <div className="font-display text-4xl font-semibold text-paper drop-shadow-lg md:text-6xl">{title}</div>
                </div>
              </div>
            )}
          </motion.div>

          <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 gap-2">
            {images.map((_, idx) => (
              <span key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === i ? "w-6 bg-gold" : "w-1.5 bg-edge"}`} />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
